import React, { useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Backdrop,
  useTheme
} from '@mui/material';
import { Instagram } from '@mui/icons-material';

interface FullScreenLoadingOverlayProps {
  isOpen: boolean;
  message?: string;
  subMessage?: string;
  showInstagramIcon?: boolean;
}

const FullScreenLoadingOverlay: React.FC<FullScreenLoadingOverlayProps> = ({
  isOpen,
  message = 'Loading...',
  subMessage = '',
  showInstagramIcon = false
}) => {
  const theme = useTheme();

  // Prevent body scrolling when overlay is open
  useEffect(() => {
    if (isOpen) {
      // Store original overflow
      const originalOverflow = document.body.style.overflow;

      // Prevent scrolling
      document.body.style.overflow = 'hidden';

      // Cleanup function to restore scrolling
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Backdrop
      open={isOpen}
      sx={{
        color: '#fff',
        zIndex: theme.zIndex.modal + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: 4,
        }}
      >
        {/* Icon */}
        {showInstagramIcon ? (
          <Instagram
            sx={{
              fontSize: 64,
              mb: 3,
              color: theme.palette.primary.main,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': {
                  opacity: 0.6,
                  transform: 'scale(1)',
                },
                '50%': {
                  opacity: 1,
                  transform: 'scale(1.05)',
                },
                '100%': {
                  opacity: 0.6,
                  transform: 'scale(1)',
                },
              },
            }}
          />
        ) : (
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              mb: 3,
              color: theme.palette.primary.main,
            }}
          />
        )}

        {/* Main message */}
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 1,
            color: '#ffffff',
          }}
        >
          {message}
        </Typography>

        {/* Sub message */}
        {subMessage && (
          <Typography
            variant="body1"
            sx={{
              maxWidth: 400,
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            {subMessage}
          </Typography>
        )}

        {/* Loading dots animation */}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            gap: 1,
          }}
        >
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                animation: `loadingDot 1.4s ease-in-out infinite both`,
                animationDelay: `${index * 0.16}s`,
                '@keyframes loadingDot': {
                  '0%, 80%, 100%': {
                    transform: 'scale(0)',
                    opacity: 0.3,
                  },
                  '40%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Backdrop>
  );
};

export default FullScreenLoadingOverlay;
